import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '../components/Layout/MainLayout';
import { useAuth } from '../hooks/useAuth';
import { useEvents } from '../hooks/useEvents';
import { EventFilterBar } from '../components/events/EventFilterBar';
import { MiniCalendar } from '../components/Common/MiniCalendar';
import { WeekViewCalendar } from '../components/events/WeekViewCalendar';
import { EventCardList } from '../components/events/EventCardList';
import { EventDetailDialog } from '../components/events/EventDetailDialog';
import { DeleteEventDialog } from '../components/events/DeleteEventDialog';
import { eventService } from '../services/eventService';
import { EventData, EventFilter } from '../types/event';
import { getTodayStr } from '../utils/dateUtils';

type CalendarView = 'month' | 'week';

const pad = (n: number) => String(n).padStart(2, '0');

const toLocalDateStr = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const getSundayOfWeek = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() - d.getDay());
  return toLocalDateStr(d);
};

const getSaturdayOfWeek = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + (6 - d.getDay()));
  return toLocalDateStr(d);
};

const now = new Date();

const EventCalendarPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin        = user?.userRole === 'admin';
  const isCompanyUser  = ['company_admin', 'hr'].includes(user?.userRole ?? '');
  const canCreateEvent = ['admin', 'company_admin', 'hr'].includes(user?.userRole ?? '');

  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [filter, setFilter] = useState<EventFilter>({});

  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [weekStart, setWeekStart] = useState<string | null>(null);
  const [weekEnd,   setWeekEnd]   = useState<string | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget]   = useState<EventData | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  // 過去イベント専用ステート
  const [pastEvents,    setPastEvents]    = useState<EventData[]>([]);
  const [isPastLoading, setIsPastLoading] = useState(false);
  const [pastFetched,   setPastFetched]   = useState(false);

  const query = calendarView === 'week' && weekStart && weekEnd
    ? {
        date_from: weekStart,
        date_to:   weekEnd,
        date:      selectedDate ?? undefined,
        limit:     50,
        ...filter,
      }
    : {
        year,
        month,
        date: selectedDate ?? undefined,
        ...filter,
      };

  const { events, calendarDots, total, isLoading, error, loadMore, refetch, updateParticipation } = useEvents(query);

  const todayStr = getTodayStr();
  const pastEventsTotal = (calendarView === 'week' || selectedDate)
    ? 0
    : calendarDots
        .filter((dot) => dot.date < todayStr)
        .reduce((sum, dot) => sum + dot.count, 0);

  const prevContextRef = useRef({ year, month, filter, weekStart });
  useEffect(() => {
    const prev = prevContextRef.current;
    if (
      prev.year !== year ||
      prev.month !== month ||
      prev.weekStart !== weekStart ||
      JSON.stringify(prev.filter) !== JSON.stringify(filter)
    ) {
      setPastEvents([]);
      setPastFetched(false);
      prevContextRef.current = { year, month, filter, weekStart };
    }
  }, [year, month, filter, weekStart]);

  const handleExpandPast = useCallback(async () => {
    if (pastFetched || isPastLoading || selectedDate || calendarView === 'week') return;
    setIsPastLoading(true);
    try {
      const data = await eventService.getEvents({
        year,
        month,
        past_only: true,
        limit: 100,
        ...filter,
      });
      setPastEvents(data.events);
    } catch {
      // サイレントフォールバック
    } finally {
      setIsPastLoading(false);
      setPastFetched(true);
    }
  }, [year, month, filter, pastFetched, isPastLoading, selectedDate]);

  const handleMonthChange = useCallback((y: number, m: number) => {
    setYear(y);
    setMonth(m);
    setSelectedDate(null);
  }, []);

  const handleWeekChange = useCallback((ws: string, we: string) => {
    setWeekStart(ws);
    setWeekEnd(we);
    setSelectedDate(null);
  }, []);

  const handleViewChange = useCallback((_: React.SyntheticEvent, newView: CalendarView) => {
    setCalendarView(newView);
    if (newView === 'week') {
      const today = getTodayStr();
      setSelectedDate(today);
      setWeekStart(getSundayOfWeek(today));
      setWeekEnd(getSaturdayOfWeek(today));
    } else {
      setSelectedDate(null);
    }
  }, []);

  const handleSwitchToMonth = useCallback(() => {
    setCalendarView('month');
    setSelectedDate(null);
  }, []);

  const handleDateClick = useCallback((date: string) => {
    setSelectedDate((prev) => (prev === date ? null : date));
  }, []);

  const handleFilterChange = useCallback((f: EventFilter) => {
    setFilter(f);
  }, []);

  const openDetail = useCallback((event: EventData) => {
    setSelectedEventId(event.id);
    setDetailOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setSelectedEventId(null);
  }, []);

  const openDelete = useCallback((event: EventData) => {
    setDeleteTarget(event);
    setDeleteDialogOpen(true);
    setDetailOpen(false);
  }, []);

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  useEffect(() => {
    const state = location.state as { successMessage?: string } | null;
    if (state?.successMessage) {
      showSnackbar(state.successMessage, 'success');
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* ページヘッダー */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={canCreateEvent ? { xs: 1.5, md: 2 } : 2}>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              企業イベントカレンダー
            </Typography>
            <Typography variant="body2" color="text.secondary">
              企業説明会・選考会・インターンシップなどのイベント情報を探せます
            </Typography>
          </Box>
          {canCreateEvent && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/events/new')}
              sx={{ display: { xs: 'none', md: 'flex' }, whiteSpace: 'nowrap', ml: 2 }}
            >
              イベント作成
            </Button>
          )}
        </Box>

        {canCreateEvent && (
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/events/new')}
            sx={{ display: { xs: 'flex', md: 'none' }, mb: 2 }}
          >
            イベント作成
          </Button>
        )}

        {/* フィルタ */}
        <EventFilterBar
          filter={filter}
          onChange={handleFilterChange}
          showTagFilter={user?.userRole === 'student'}
          showAdminFilter={isAdmin}
          showCompanyFilter={isCompanyUser}
        />

        {/* 月/週 タブ */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
          <Tabs
            value={calendarView}
            onChange={handleViewChange}
            sx={{ minHeight: 40 }}
          >
            <Tab
              label="月ビュー"
              value="month"
              sx={{ minHeight: 40, fontSize: '0.85rem', fontWeight: 600 }}
            />
            <Tab
              label="週ビュー"
              value="week"
              sx={{ minHeight: 40, fontSize: '0.85rem', fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* ─── 週ビュー：フル幅レイアウト ─── */}
        {calendarView === 'week' && weekStart && weekEnd && (
          <WeekViewCalendar
            weekStart={weekStart}
            weekEnd={weekEnd}
            calendarDots={calendarDots}
            events={events}
            isLoading={isLoading}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
            onWeekChange={handleWeekChange}
            onSwitchToMonth={handleSwitchToMonth}
            onEventDetail={openDetail}
          />
        )}

        {/* ─── 月ビュー：左右グリッドレイアウト ─── */}
        {calendarView === 'month' && (
          <Grid container spacing={3}>
            {/* ミニカレンダー */}
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <MiniCalendar
                  year={year}
                  month={month}
                  calendarDots={calendarDots}
                  selectedDate={selectedDate}
                  onDateClick={handleDateClick}
                  onMonthChange={handleMonthChange}
                />
              </Paper>
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<EventBusyIcon fontSize="small" />}
                  onClick={() => setSelectedDate(null)}
                  disabled={!selectedDate}
                  sx={{
                    whiteSpace: 'nowrap',
                    color: selectedDate ? 'text.primary' : 'text.disabled',
                    borderColor: selectedDate ? 'text.secondary' : 'divider',
                  }}
                >
                  日付フィルターをクリア
                </Button>
              </Box>
            </Grid>

            {/* イベントカード一覧 */}
            <Grid item xs={12} md={8}>
              <EventCardList
                events={events}
                total={total}
                pastEventsTotal={pastEventsTotal}
                pastEvents={selectedDate ? [] : pastEvents}
                isPastLoading={isPastLoading}
                isLoading={isLoading}
                error={error}
                onDetail={openDetail}
                onLoadMore={loadMore}
                onExpandPast={handleExpandPast}
              />
            </Grid>
          </Grid>
        )}
      </Container>

      {/* イベント詳細ダイアログ */}
      <EventDetailDialog
        eventId={selectedEventId}
        userRole={user?.userRole ?? ''}
        open={detailOpen}
        onClose={closeDetail}
        onDelete={openDelete}
        onParticipationChange={updateParticipation}
        onSnackbar={showSnackbar}
      />

      {/* 削除確認ダイアログ */}
      <DeleteEventDialog
        event={deleteTarget}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          setDeleteDialogOpen(false);
          refetch();
          if (deleteTarget) {
            setPastEvents((prev) => prev.filter((e) => e.id !== deleteTarget.id));
          }
        }}
        onSnackbar={showSnackbar}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 5000 : 3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default EventCalendarPage;
